import { Alert, Center, Loader, SimpleGrid, Stack, Text } from "@mantine/core";
import { useInViewport } from "@mantine/hooks";
import { createFileRoute } from "@tanstack/react-router";
import EntityNotFound from "@webapp/components/EntityNotFound";
import PageWithHeading from "@webapp/components/layout/PageWithHeading";
import NewsCard from "@webapp/components/NewsCard";
import { ServerCrash } from "lucide-react";
import { useEffect } from "react";
import { useNews } from "@/app/src/hooks/dataQueries";
import { getPublishedNewsFn } from "@webapp/server/functions/news";

export const Route = createFileRoute("/_layout/news/")({
  loader: async () => {
    const page = await getPublishedNewsFn({ data: { limit: 8 } });
    return { news: page.items };
  },
  component: RouteComponent,
});

const BATCH_SIZE = 8;

function RouteComponent() {
  const { news: initialNews } = Route.useLoaderData();
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, error, isLoading } = useNews({
    limit: BATCH_SIZE,
    initialItems: initialNews,
  });
  const news = data?.pages.flatMap((page) => page.items) ?? [];

  // Infinite scroll logic using Mantine's useInViewport
  const { inViewport, ref: loaderRef } = useInViewport<HTMLAnchorElement>();
  useEffect(() => {
    if (inViewport && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inViewport, hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (data && news.length === 0) {
    return (
      <EntityNotFound
        entityName="News Beiträge"
        title="Ladefehler"
        description="Es konnten keine News geladen werden."
      />
    );
  }

  return (
    <PageWithHeading title="News Beiträge">
      <Stack>
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          {news.map((post, index) => (
            <NewsCard
              key={post.id}
              {...post}
              ref={index <= news.length - BATCH_SIZE ? loaderRef : undefined}
            />
          ))}
        </SimpleGrid>
        {(isLoading || isFetchingNextPage) && (
          <Stack align="center" gap="md">
            <Loader size="xl" />
            <Text c="dimmed">Lade News...</Text>
          </Stack>
        )}
        {error && (
          <Center py="xl">
            <Alert
              variant="light"
              color="red"
              radius="md"
              title="Fehler beim Laden der News"
              icon={<ServerCrash />}
            >
              {error.message}
            </Alert>
          </Center>
        )}
      </Stack>
    </PageWithHeading>
  );
}
